import { Search, Filter, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { NewsFilterTab } from '@/hooks/useNewsIntelligence';
import { SortOption, DateRangePreset } from '@/components/news/NewsIntelligenceFilters';

const SIGNAL_TYPES = [
  { value: 'funding', label: 'Funding' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'clinical_data', label: 'Clinical Data' },
  { value: 'market_trend', label: 'Market Trend' },
  { value: 'leadership', label: 'Leadership' },
  { value: 'competitor', label: 'Competitor News' },
];

const PRIORITY_OPTIONS = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const DATE_OPTIONS: { value: DateRangePreset; label: string }[] = [
  { value: '7days', label: 'Last 7 days' },
  { value: '30days', label: 'Last 30 days' },
  { value: 'all', label: 'All time' },
];

const TABS: { value: NewsFilterTab; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'opportunities', label: 'Opportunities' },
  { value: 'competitors', label: 'Competitors' },
  { value: 'priority', label: 'Priority' },
];

interface NewsFilterSidebarProps {
  activeTab: NewsFilterTab;
  onTabChange: (tab: NewsFilterTab) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  signalType: string;
  onSignalTypeChange: (value: string) => void;
  priority: string;
  onPriorityChange: (value: string) => void;
  hasProspect: boolean;
  onHasProspectChange: (value: boolean) => void;
  dateRange: DateRangePreset;
  onDateRangeChange: (value: DateRangePreset) => void;
  sortBy: SortOption;
  onSortChange: (value: SortOption) => void;
  onClearAll: () => void;
  hasActiveFilters: boolean;
  totalCount: number;
}

export function NewsFilterSidebar({
  activeTab,
  onTabChange,
  searchTerm,
  onSearchChange,
  signalType,
  onSignalTypeChange,
  priority,
  onPriorityChange,
  hasProspect,
  onHasProspectChange,
  dateRange,
  onDateRangeChange,
  sortBy,
  onSortChange,
  onClearAll,
  hasActiveFilters,
  totalCount,
}: NewsFilterSidebarProps) {
  return (
    <div className="space-y-4 p-4 bg-card rounded-lg border">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">Filters</span>
          <Badge variant="secondary">{totalCount.toLocaleString()} articles</Badge>
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={onClearAll} className="h-8">
            <X className="h-4 w-4 mr-1" />
            Clear All
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search articles..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Tab buttons */}
      <div className="flex flex-wrap gap-1 p-1 bg-muted rounded-lg">
        {TABS.map((tab) => (
          <Button
            key={tab.value}
            variant={activeTab === tab.value ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onTabChange(tab.value)}
            className="h-7 text-xs flex-1"
          >
            {tab.label}
          </Button>
        ))}
      </div>

      <Accordion type="multiple" defaultValue={['signal', 'priority', 'date']} className="space-y-2">
        {/* Signal Type */}
        <AccordionItem value="signal" className="border rounded-lg px-3">
          <AccordionTrigger className="text-sm font-medium py-3">Signal Type</AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-2">
              {SIGNAL_TYPES.map((st) => (
                <div key={st.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`signal-${st.value}`}
                    checked={signalType === st.value}
                    onCheckedChange={(checked) => onSignalTypeChange(checked ? st.value : 'all')}
                  />
                  <label htmlFor={`signal-${st.value}`} className="text-sm leading-none">{st.label}</label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Priority */}
        <AccordionItem value="priority" className="border rounded-lg px-3">
          <AccordionTrigger className="text-sm font-medium py-3">Priority</AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-2">
              {PRIORITY_OPTIONS.map((p) => (
                <div key={p.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`priority-${p.value}`}
                    checked={priority === p.value}
                    onCheckedChange={(checked) => onPriorityChange(checked ? p.value : 'all')}
                  />
                  <label htmlFor={`priority-${p.value}`} className="text-sm leading-none">{p.label}</label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Date Range */}
        <AccordionItem value="date" className="border rounded-lg px-3">
          <AccordionTrigger className="text-sm font-medium py-3">Date Range</AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-2">
              {DATE_OPTIONS.map((d) => (
                <div key={d.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`date-${d.value}`}
                    checked={dateRange === d.value}
                    onCheckedChange={(checked) => onDateRangeChange(checked ? d.value : 'all')}
                  />
                  <label htmlFor={`date-${d.value}`} className="text-sm leading-none">{d.label}</label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Options */}
        <AccordionItem value="options" className="border rounded-lg px-3">
          <AccordionTrigger className="text-sm font-medium py-3">Options</AccordionTrigger>
          <AccordionContent className="pb-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Has Prospect</Label>
              <Switch checked={hasProspect} onCheckedChange={onHasProspectChange} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Sort by</Label>
              <Select value={sortBy} onValueChange={(v) => onSortChange(v as SortOption)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest first</SelectItem>
                  <SelectItem value="priority">Highest priority</SelectItem>
                  <SelectItem value="relevance">Highest relevance</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
